import Check from "@mui/icons-material/Check";
import { Typography } from "@mui/material";
import { Box, Stack, useTheme } from "@mui/system";
import { mdHeight, SubNavAppBar, xsHeight } from "components/AppBar";
import { ExportDataButton } from "components/ExportDataButton";
import { InfoIconTooltip } from "components/InfoIconTooltip";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { TagsDisplay } from "components/TagsDisplay";
import { TruncateTypography } from "components/TruncateTypography";
import { WasteTagsColumnHeader } from "components/WasteTagsColumnHeader";
import { getUtcDisplayDate } from "encamp-shared/src/utils/date";
import { WasteLinesUnifiedQuery, WasteLinesUnifiedQueryVariables } from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useSelectedIdInUrl } from "hooks/useSelectedIdInUrl";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";
import { useParams } from "react-router";
import { formatNumber } from "util/formatNumber";

import { WASTE_LINES_UNIFIED_QUERY } from "./api";
import { ImportButton } from "./ImportButton";
import { ManifestLineItemDetail } from "./ManifestLineItemDetail";
import { StaffActionsMenu } from "./StaffActionsMenu";
import { YearAutocompletePicker } from "./YearAutocompletePIcker";

type WasteLineUnifiedRow = NonNullable<
  WasteLinesUnifiedQuery["wasteLinesUnified"]
>["items"][number];

export function ManifestLineItems() {
  const theme = useTheme();
  const { tenantId } = useParams<{ tenantId: string }>();
  const { selectedId, pushIdToUrl, popIdFromUrl } = useSelectedIdInUrl("wasteLineUnifiedId");
  const { isStaff } = useCurrentUser();
  const [shippedDateYear, setShippedDateYear] = useLocalStorageState<number>(
    "waste-manifest-line-items-shipped-date-year",
    {
      defaultValue: DateTime.now().year,
    }
  );
  const [selectedRows, setSelectedRows] = useState<WasteLineUnifiedRow[]>([]);

  const filename = useMemo(() => `waste-manifests-${shippedDateYear}`, [shippedDateYear]);

  const defaultSearch = useMemo(
    () => `tenantId:${tenantId} shippedDate:${shippedDateYear}`,
    [tenantId, shippedDateYear]
  );

  useBreadcrumb([
    {
      label: "Waste",
      to: tenantId ? `/o/${tenantId}/waste` : undefined,
    },
    {
      label: "Manifest Line Items",
    },
  ]);

  const columns: OmnisearchGridColDef<WasteLineUnifiedRow>[] = useMemo(() => {
    const vendorAmountTooltip =
      "For volumetric conversions, if no density is provided, Encamp uses the density of water as USEPA does in eManifest. If density is provided as a range (e.g. 0.8-1.0), Encamp uses the midpoint when converting to pounds.";

    return [
      {
        field: "lineItem",
        headerName: "Line Item",
        minWidth: 100,
        flex: 2,
        renderCell: ({ row: { willNotReport }, value }) => {
          return (
            <Box>
              <TruncateTypography variant="body2">{value}</TruncateTypography>
              {willNotReport && (
                <Typography variant="caption" color="text.secondary">
                  Not Reportable
                </Typography>
              )}
            </Box>
          );
        },
      },
      {
        field: "vendorAmount",
        headerName: "Amount (Vendor)",
        renderHeader: () => (
          <Stack direction="row" spacing={0.5} alignItems="center">
            Amount (Vendor)
            <InfoIconTooltip tooltip={vendorAmountTooltip} />
          </Stack>
        ),
        filterKeyType: "number",
        minWidth: 80,
        flex: 1,
        valueGetter: (_, row) => {
          const amount = formatNumber(row.vendorAmount);
          return amount ? `${amount} lbs` : "";
        },
      },
      {
        field: "eManifestAmount",
        headerName: "Amount (e‑Manifest)",
        filterKeyType: "number",
        minWidth: 80,
        flex: 1,
        valueGetter: (_, row) => {
          const amount = formatNumber(row.eManifestAmount);
          return amount ? `${amount} lbs` : "";
        },
      },
      {
        field: "shippedDate",
        headerName: "Ship Date",
        filterKeyType: "date",
        flex: 1,
        valueGetter: (_, row) => {
          return getUtcDisplayDate(row.shippedDate, "med");
        },
      },
      {
        field: "facility.name",
        headerName: "Facility",
        filterKeyType: "wasteFacility",
        flex: 1,
        valueGetter: (_, { facility }) => {
          return facility?.name;
        },
      },
      {
        field: "epaId",
        headerName: "EPA ID",
        flex: 1,
      },
      {
        field: "manifestTrackingNumber",
        headerName: "Manifest #",
        flex: 1,
      },
      {
        field: "profileNumber",
        headerName: "Profile #",
        flex: 1,
      },
      {
        field: "managementMethodCode",
        headerName: "Management Method Code",
        flex: 1,
      },
      {
        field: "federalWasteCodes",
        headerName: "Federal Waste Codes",
        flex: 1,
        valueGetter: (_, { federalWasteCodes }) => {
          return federalWasteCodes?.join(", ");
        },
      },
      {
        field: "stateWasteCodes",
        headerName: "State Waste Codes",
        flex: 1,
        valueGetter: (_, { stateWasteCodes }) => {
          return stateWasteCodes?.join(", ");
        },
      },
      { field: "wasteStream", headerName: "Waste Stream", flex: 1 },
      {
        field: "vendor",
        headerName: "Vendor",
        flex: 1,
      },
      {
        field: "isEManifest",
        headerName: "e‑Manifest",
        filterKeyType: "boolean",
        sortable: false,
        flex: 1,
        renderCell: ({ value }) => {
          if (value) {
            return <Check />;
          } else {
            return null;
          }
        },
      },
      {
        field: "willNotReport",
        headerName: "Not Reportable",
        filterKeyType: "boolean",
        sortable: false,
        flex: 1,
        renderCell: ({ value }) => {
          if (value === true) {
            return <Check />;
          }
          return null;
        },
      },
      {
        field: "tags",
        headerName: "Tags",
        filterKeyType: "wasteTags",
        renderHeader: () => <WasteTagsColumnHeader />,
        flex: 1,
        renderCell: ({ row: { tags } }) => {
          return <TagsDisplay tags={tags} />;
        },
      },
    ];
  }, []);

  return (
    <Box>
      <SubNavAppBar
        sx={{
          top: {
            xs: theme.spacing(xsHeight + 6.1),
            md: theme.spacing(mdHeight + 6.1),
          },
          py: 2,
          px: 3,
          borderBottom: "1px solid",
          borderColor: theme.palette.divider,
        }}
      >
        <Stack direction={{ xs: "column", md: "row" }} alignItems="start" spacing={2}>
          <Box sx={{ width: 80 }}>
            <YearAutocompletePicker
              label="Year"
              minYear={2018}
              value={shippedDateYear}
              onChange={setShippedDateYear}
            />
          </Box>
        </Stack>
      </SubNavAppBar>
      <Box sx={{ p: 3 }}>
        <OmnisearchDataGrid<
          WasteLinesUnifiedQuery,
          WasteLinesUnifiedQueryVariables,
          WasteLineUnifiedRow
        >
          isURLDriven
          persistenceKey="manifest-line-items"
          withPadding={false}
          columns={columns}
          dataQuery={WASTE_LINES_UNIFIED_QUERY}
          defaultSearch={defaultSearch}
          getItems={(data) => data.wasteLinesUnified?.items ?? []}
          getCount={(data) => data.wasteLinesUnified?.count ?? 0}
          noDataMessage="No manifest line items found."
          initialState={{
            sorting: { sortModel: [{ field: "shippedDate", sort: "desc" }] },
          }}
          rowHeight={"auto"}
          checkboxSelection={isStaff}
          disableRowSelectionOnClick
          selectedIds={selectedRows.map((r) => r.id)}
          onSelectedRowsChanged={(rows) => setSelectedRows(rows)}
          onRowClick={({ row: { id } }) => pushIdToUrl(id)}
          showFilters
          showManageColumns
          additionalFilterColumns={[
            {
              header: "Has Vendor Data",
              key: "isVendor",
              filterKeyType: "boolean",
            },
            {
              header: "e‑Manifest",
              key: "isEManifest",
              filterKeyType: "boolean",
            },
          ]}
          columnVisibilityModel={{
            federalWasteCodes: false,
            stateWasteCodes: false,
            wasteStream: false,
            willNotReport: false,
          }}
          commandButtons={({ search }) => [
            ...(isStaff ? [<ImportButton key="import" />] : []),
            <ExportDataButton
              key="export"
              fileName={filename}
              tenantId={tenantId}
              filter={{ search }}
              exportType="wasteManifestLines"
              iconOnly
            />,
            ...(isStaff
              ? [
                  <StaffActionsMenu
                    key="staff"
                    selectedRows={selectedRows}
                    onCompleted={() => {
                      // Clear selected rows after actions are completed
                      setSelectedRows([]);
                    }}
                  />,
                ]
              : []),
          ]}
        />
      </Box>
      <ManifestLineItemDetail lineItemId={selectedId} open={!!selectedId} onClose={popIdFromUrl} />
    </Box>
  );
}
